import React from "react";
import { getClientBrandMeta } from "../socialBrand";

interface Props {
  activePlatform: string;
  showGoogleRatings: boolean;
  criticalCount: number;
  clientSlug: string;
}

export function SocialActionPlanSection({
  activePlatform,
  showGoogleRatings,
  criticalCount,
  clientSlug,
}: Props) {
  const brandMeta = getClientBrandMeta(clientSlug);

  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  return (
    <section id="section-social-plan" className="social-editorial-section">
      <div className="social-section-tag">SIGUIENTE PASO</div>
      <h2 className="social-section-title">
        {isMetaIg
          ? "Tu plan de acción para Instagram"
          : isMetaFb
          ? "Tu plan de acción para Facebook"
          : isGoogle
          ? "Tu plan de acción para Google BP"
          : "Tu plan de acción multicanal"}
      </h2>
      <p className="social-section-subtitle">
        Una tarea por área, generada con las cifras de reputación observadas.
      </p>

      <div className="social-plan-grid">
        <div className="social-trend-box">
          <span style={{ display: "inline-block", fontSize: "10.5px", padding: "2px 8px", borderRadius: "999px", background: "rgba(125, 150, 255, 0.15)", color: "#9cb5ff", fontWeight: 700, marginBottom: "8px" }}>
            Comunicaciones
          </span>
          <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: 700, color: "var(--text, #f6f7fb)", lineHeight: 1.35 }}>
            Aprobar y responder comentarios críticos
          </h4>
          <p style={{ margin: 0, fontSize: "11.5px", color: "var(--muted, #8e96a8)", lineHeight: 1.4 }}>
            {criticalCount} comentarios o reseñas prioritarias sin respuesta en {platformBadgeLabel}
          </p>
        </div>

        <div className="social-trend-box">
          <span style={{ display: "inline-block", fontSize: "10.5px", padding: "2px 8px", borderRadius: "999px", background: "rgba(46, 213, 115, 0.15)", color: "#7bed9f", fontWeight: 700, marginBottom: "8px" }}>
            Marketing
          </span>
          <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: 700, color: "var(--text, #f6f7fb)", lineHeight: 1.35 }}>
            {isGoogle
              ? `Pedir reseñas a ${brandMeta.subjectNounPlural} satisfechos`
              : `Destacar los atributos mejor valorados en contenidos de ${platformBadgeLabel}`}
          </h4>
          <p style={{ margin: 0, fontSize: "11.5px", color: "var(--muted, #8e96a8)", lineHeight: 1.4 }}>
            {isGoogle
              ? "Promover reseñas positivas en Google para aumentar la nota media"
              : `Potenciar los temas más elogiados por la comunidad en ${platformBadgeLabel}`}
          </p>
        </div>

        <div className="social-trend-box">
          <span style={{ display: "inline-block", fontSize: "10.5px", padding: "2px 8px", borderRadius: "999px", background: "rgba(247, 189, 91, 0.15)", color: "#ffd88a", fontWeight: 700, marginBottom: "8px" }}>
            Operaciones
          </span>
          <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: 700, color: "var(--text, #f6f7fb)", lineHeight: 1.35 }}>
            {showGoogleRatings
              ? "Revisar tiempos de espera y atención en causas críticas"
              : "Revisar causas de fricción y dudas frecuentes con el equipo"}
          </h4>
          <p style={{ margin: 0, fontSize: "11.5px", color: "var(--muted, #8e96a8)", lineHeight: 1.4 }}>
            {showGoogleRatings
              ? "Monitorear aspectos con menor calificación para recuperar la confianza"
              : `Corregir las principales causas de reclamo detectadas en ${platformBadgeLabel}`}
          </p>
        </div>

        <div className="social-trend-box">
          <span style={{ display: "inline-block", fontSize: "10.5px", padding: "2px 8px", borderRadius: "999px", background: "rgba(255, 92, 92, 0.15)", color: "#ff8e8e", fontWeight: 700, marginBottom: "8px" }}>
            Dirección
          </span>
          <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: 700, color: "var(--text, #f6f7fb)", lineHeight: 1.35 }}>
            {showGoogleRatings
              ? "Seguimiento semanal de nota en Google BP"
              : `Seguimiento semanal de reputación e interacción en ${platformBadgeLabel}`}
          </h4>
          <p style={{ margin: 0, fontSize: "11.5px", color: "var(--muted, #8e96a8)", lineHeight: 1.4 }}>
            {showGoogleRatings
              ? "Control de reputación en Google BP y evolución mensual"
              : "Monitorear la satisfacción de la comunidad y volumen de interacciones"}
          </p>
        </div>
      </div>

      {/* Bloque: QUÉ OBTIENE CADA ÁREA */}
      <div style={{ marginTop: "16px" }}>
        <div className="social-section-tag" style={{ fontSize: "10.5px" }}>QUÉ OBTIENE CADA ÁREA</div>
        <h3 className="social-section-title" style={{ fontSize: "22px", margin: "2px 0 16px 0" }}>
          Un panel, tres áreas ganando tiempo
        </h3>

        <div className="social-value-grid">
          <div className="social-trend-box">
            <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
              Marketing
            </h4>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--muted, #8e96a8)", lineHeight: 1.5 }}>
              Descubre qué mensajes elogian los clientes y qué anuncios generan fricción, antes de seguir invirtiendo en pauta.
            </p>
          </div>

          <div className="social-trend-box">
            <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
              Comunicaciones
            </h4>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--muted, #8e96a8)", lineHeight: 1.5 }}>
              Responde primero lo urgente, con borradores en el tono de la marca y enlaces directos para publicar.
            </p>
          </div>

          <div className="social-trend-box">
            <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
              Operaciones y dirección
            </h4>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--muted, #8e96a8)", lineHeight: 1.5 }}>
              Ve en qué proceso o canal se pierde la confianza, y si la nota sube o baja mes a mes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
